"use client";

import { Comedian, getComedianByID } from "@/data/comedians";

import { Database } from "@punchup/data/supabase.types";
import { SentryFeatureTags, logException } from "@/analytics/sentry";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { getAuthorizedComediansAndVenuesFrontEnd } from "@punchup/data/legacy";
import { useQuery } from "@tanstack/react-query";

interface ManagedComedians {
  /** Full comedian records the current user manages (for display). */
  comedians: Comedian[];
  /** Raw comedian IDs the user manages (for authorization checks). */
  managedComedianIds: string[];
}

interface UseManagedComediansResult extends ManagedComedians {
  isLoading: boolean;
}

const EMPTY: ManagedComedians = { comedians: [], managedComedianIds: [] };

/**
 * Returns the artists (comedians) the current user manages, sourced from the
 * same `comedian_dashboard_admins`-backed endpoint as the dashboard comedian
 * switcher (`/api/user-status`). Used on the public video page to power the
 * "comment as [artist]" toggle and to decide edit/delete visibility on
 * artist-authored comments.
 *
 * Imported by direct path (not the `@/hooks` barrel) to keep the heavy
 * `@punchup/venues/client` dashboard bundle out of the public comment graph.
 */
export function useManagedComedians(): UseManagedComediansResult {
  const supabase = createClientComponentClient<Database>();

  const { data, isLoading } = useQuery({
    queryKey: ["managed-comedians"],
    queryFn: async (): Promise<ManagedComedians> => {
      try {
        const { comedians: ids } =
          await getAuthorizedComediansAndVenuesFrontEnd(supabase);
        const records = await Promise.all(ids.map((id) => getComedianByID(id)));
        return {
          managedComedianIds: ids,
          comedians: records.filter((c): c is Comedian => c !== null),
        };
      } catch (error) {
        // Fail open: non-managers / signed-out users simply have no artists to
        // act as. Swallowing here (rather than letting the query error) keeps
        // the contract — empty, not an error — and avoids retry storms on every
        // signed-out video-page view. Logged at "info" so the fail-open UX is
        // preserved while unexpected backend failures (e.g. a broken endpoint)
        // remain visible in Sentry rather than disappearing silently.
        logException(error as Error, {
          tags: {
            feature: SentryFeatureTags.ComedianManager,
            action: "load_managed_comedians",
          },
          level: "info",
        });
        return EMPTY;
      }
    },
    staleTime: 30_000,
  });

  return {
    comedians: data?.comedians ?? EMPTY.comedians,
    managedComedianIds: data?.managedComedianIds ?? EMPTY.managedComedianIds,
    isLoading,
  };
}
